import React from 'react';
import { FaCalendarAlt, FaMapMarkerAlt, FaRegClock } from 'react-icons/fa';
import LocationPicker from '../../../../../components/LocationPicker.jsx';

/**
 * Editable mirror of ReadOnlyEventDetailsCard. All inputs are pre-filled from
 * `formData` (pulled from the DB) and edits are pushed up via the change handlers.
 */
const EditableEventDetailsCard = ({
  formData,
  onFieldChange,
  packages,
  onPackageChange,
  activeTab,
  onTabChange,
}) => {
  if (!formData) return null;

  const tabs = ['About', 'Packages', 'Terms & Conditions'];

  return (
    <div className="event-details-card">
      <div className="event-details-card__info-bar admin-editable__info-bar">
        <div className="event-details-card__info-item">
          <FaCalendarAlt />
          <input
            type="date"
            className="admin-editable__inline-input"
            value={formData.start_date || ''}
            onChange={(e) => onFieldChange('start_date', e.target.value)}
          />
          <span className="admin-editable__inline-sep">to</span>
          <input
            type="date"
            className="admin-editable__inline-input"
            value={formData.end_date || ''}
            onChange={(e) => onFieldChange('end_date', e.target.value)}
          />
        </div>
        <div className="event-details-card__info-item" style={{ alignItems: 'flex-start' }}>
          <FaMapMarkerAlt style={{ marginTop: 8 }} />
          <div style={{ flex: 1 }}>
            <LocationPicker
              value={formData.location || null}
              onChange={(locationData) => onFieldChange('location', locationData)}
              placeholder="Search for event location..."
            />
          </div>
        </div>
        <div className="event-details-card__info-item">
          <FaRegClock />
          <input
            type="time"
            className="admin-editable__inline-input"
            value={formData.time || ''}
            onChange={(e) => onFieldChange('time', e.target.value)}
          />
          <span className="admin-editable__inline-sep">to</span>
          <input
            type="time"
            className="admin-editable__inline-input"
            value={formData.endtime || ''}
            onChange={(e) => onFieldChange('endtime', e.target.value)}
          />
        </div>
      </div>

      <div className="event-details-card__tabs">
        {tabs.map((label, index) => (
          <button
            key={label}
            type="button"
            className={
              index === activeTab
                ? 'event-details-card__tab event-details-card__tab--active'
                : 'event-details-card__tab'
            }
            onClick={() => onTabChange(index)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="event-details-card__tab-content">
        {activeTab === 0 && (
          <div className="admin-editable__section">
            <h3>About This Event</h3>
            <label className="admin-editable__field">
              <span>Event Name</span>
              <input
                type="text"
                value={formData.event_name || ''}
                onChange={(e) => onFieldChange('event_name', e.target.value)}
              />
            </label>
            <label className="admin-editable__field">
              <span>Event Type</span>
              <input
                type="text"
                value={formData.event_type || ''}
                onChange={(e) => onFieldChange('event_type', e.target.value)}
              />
            </label>
            <label className="admin-editable__field">
              <span>Capacity (Total Tickets)</span>
              <input
                type="number"
                value={formData.capacity ?? ''}
                onChange={(e) => onFieldChange('capacity', e.target.value)}
              />
            </label>
            <label className="admin-editable__field">
              <span>Description</span>
              <textarea
                rows={5}
                value={formData.description || ''}
                onChange={(e) => onFieldChange('description', e.target.value)}
              />
            </label>
          </div>
        )}

        {activeTab === 1 && (
          <div className="admin-editable__section">
            <h3>Packages</h3>
            {Array.isArray(packages) && packages.length > 0 ? (
              packages.map((pkg, index) => (
                <div key={pkg.package_id ?? index} className="admin-editable__package">
                  <label className="admin-editable__field">
                    <span>Package Name</span>
                    <input
                      type="text"
                      value={pkg.name || ''}
                      onChange={(e) => onPackageChange(index, 'name', e.target.value)}
                    />
                  </label>
                  <label className="admin-editable__field">
                    <span>Description</span>
                    <textarea
                      rows={2}
                      value={pkg.description || ''}
                      onChange={(e) => onPackageChange(index, 'description', e.target.value)}
                    />
                  </label>
                  <div className="admin-editable__package-row">
                    <label className="admin-editable__field">
                      <span>Price</span>
                      <input
                        type="number"
                        value={pkg.price ?? ''}
                        onChange={(e) => onPackageChange(index, 'price', e.target.value)}
                      />
                    </label>
                    <label className="admin-editable__field">
                      <span>Number of Tickets</span>
                      <input
                        type="number"
                        value={pkg.max_tickets ?? ''}
                        onChange={(e) => onPackageChange(index, 'max_tickets', e.target.value)}
                      />
                    </label>
                    <label className="admin-editable__field">
                      <span>Duration (days)</span>
                      <input
                        type="number"
                        value={pkg.duration ?? ''}
                        onChange={(e) => onPackageChange(index, 'duration', e.target.value)}
                      />
                    </label>
                  </div>
                  <p className="admin-editable__package-note">
                    Valid {pkg.valid_from_date || 'TBA'} to {pkg.valid_to_date || 'TBA'}
                    {' · '}
                    {pkg.valid_from_time || 'TBA'} to {pkg.valid_to_time || 'TBA'}
                  </p>
                </div>
              ))
            ) : (
              <p>No packages for this event.</p>
            )}
          </div>
        )}

        {activeTab === 2 && (
          <div className="admin-editable__section">
            <h3>Terms &amp; Conditions</h3>
            <label className="admin-editable__field">
              <span>Terms &amp; Conditions</span>
              <textarea
                rows={8}
                value={formData.terms_and_conditions || ''}
                onChange={(e) => onFieldChange('terms_and_conditions', e.target.value)}
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditableEventDetailsCard;
